<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-box">
      <div class="modal-header-row">
        <h3 class="modal-title">Transfer Balance</h3>
        <button class="modal-close" type="button" aria-label="Close" @click="close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="#022326" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- Source -> Destination -->
      <div class="direction-row">
        <div class="balance-box" :class="source.variant">
          <div class="balance-box-label">Available {{ source.label }}</div>
          <div class="balance-box-amount">IDR {{ formatNumber(source.amount) }}</div>
        </div>
        <button class="swap-btn" type="button" aria-label="Swap transfer direction" @click="swapDirection">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 3l4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" stroke="#1c9762" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="balance-box" :class="destination.variant">
          <div class="balance-box-label">Available {{ destination.label }}</div>
          <div class="balance-box-amount">IDR {{ formatNumber(destination.amount) }}</div>
        </div>
      </div>

      <!-- Amount -->
      <div class="amount-box">
        <div class="amount-field">
          <label class="amount-label" for="transfer-amount">Amount Transfer</label>
          <div class="amount-input-row">
            <span>IDR</span>
            <input id="transfer-amount" :value="formattedAmount" inputmode="numeric" placeholder="0"
              class="amount-input" @input="onAmountInput" />
          </div>
        </div>
        <button class="all-amount-btn" type="button" @click="amount = source.amount || 0">
          All amount
        </button>
      </div>
      <div v-if="exceedsBalance" class="amount-error">
        Amount exceeds available {{ source.label }}
      </div>

      <button class="continue-btn" type="button" :disabled="!canContinue || submitting" @click="submit">
        {{ submitting ? "Processing..." : "Continue to Payment" }}
      </button>
    </div>
  </div>
</template>

<script>
const BALANCES = {
  dashboard: { label: "Dashboard Balance", variant: "is-dashboard" },
  api: { label: "API Balance", variant: "is-api" },
};

export default {
  name: "TransferBalanceModal",
  emits: ["close", "submit"],
  props: {
    show: Boolean,
    dashboardBalance: {
      type: Number,
      default: null,
    },
    apiBalance: {
      type: Number,
      default: null,
    },
    // Which balance the transfer starts from: "dashboard" | "api"
    initialSource: {
      type: String,
      default: "dashboard",
    },
    // True while the transfer request is in progress
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sourceKey: this.initialSource,
      amount: 0,
    };
  },
  computed: {
    destinationKey() {
      return this.sourceKey === "dashboard" ? "api" : "dashboard";
    },
    source() {
      return { ...BALANCES[this.sourceKey], amount: this.balanceOf(this.sourceKey) };
    },
    destination() {
      return { ...BALANCES[this.destinationKey], amount: this.balanceOf(this.destinationKey) };
    },
    formattedAmount() {
      return this.amount ? this.formatNumber(this.amount) : "";
    },
    exceedsBalance() {
      return this.amount > (this.source.amount || 0);
    },
    canContinue() {
      return this.amount > 0 && !this.exceedsBalance;
    },
  },
  watch: {
    show(isOpen) {
      if (isOpen) {
        this.sourceKey = this.initialSource;
        this.amount = 0;
      }
    },
  },
  methods: {
    balanceOf(key) {
      return key === "dashboard" ? this.dashboardBalance : this.apiBalance;
    },
    swapDirection() {
      this.sourceKey = this.destinationKey;
    },
    onAmountInput(event) {
      const digits = event.target.value.replace(/\D/g, "");
      this.amount = digits ? Number(digits) : 0;
      event.target.value = this.formattedAmount;
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "-";
      return Number(value).toLocaleString("en-US");
    },
    close() {
      this.$emit("close");
    },
    submit() {
      if (!this.canContinue || this.submitting) return;
      this.$emit("submit", {
        from: this.sourceKey,
        to: this.destinationKey,
        amount: this.amount,
      });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 35, 38, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-box {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  color: #022326;
}

.modal-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: #022326;
  margin: 0;
}

.modal-close {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f2f4f4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.direction-row {
  display: flex;
  align-items: center;
  position: relative;
  gap: 24px;
}

.balance-box {
  flex: 1;
  min-width: 0;
  border-radius: 16px;
  border: 1px solid transparent;
  padding: 24px 32px;
}

.balance-box.is-dashboard {
  background: #eaf1fb;
  border-color: #cfdcef;
}

.balance-box.is-api {
  background: #e8f8ee;
  border-color: #c6ebd5;
}

.balance-box-label {
  font-size: 14px;
  line-height: 18px;
  color: #4b5b5d;
}

.balance-box-amount {
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: #3d5457;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.swap-btn {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.amount-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 32px;
  border-radius: 16px;
  border: 1px solid #e0e3e3;
  background: #f4f5f5;
}

.amount-label {
  font-size: 14px;
  line-height: 18px;
  color: #4b5b5d;
  margin: 0;
}

.amount-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  color: #3d5457;
}

.amount-input {
  border: none;
  background: transparent;
  outline: none;
  font: inherit;
  color: inherit;
  width: 100%;
  min-width: 0;
  padding: 0;
}

.amount-input::placeholder {
  color: #3d5457;
}

.all-amount-btn {
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #1c9762;
  font-family: inherit;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
}

.amount-error {
  margin-top: -16px;
  font-size: 12px;
  color: #f04438;
}

.continue-btn {
  height: 40px;
  width: 100%;
  border-radius: 100px;
  background: linear-gradient(180deg, #43bb75 0%, #1c9762 100%);
  border: 1px solid #128050;
  box-shadow: inset 0 2px 1px 0 #ffffff40;
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}

.continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 576px) {
  .direction-row {
    flex-direction: column;
    gap: 16px;
  }

  .balance-box {
    width: 100%;
    padding: 16px 20px;
  }

  .swap-btn {
    transform: translate(-50%, -50%) rotate(90deg);
  }

  .amount-box {
    padding: 16px 20px;
  }
}
</style>
